import { motion } from "framer-motion";
import { Mail, Phone, Linkedin, ArrowRight, ArrowUpRight, Handshake, MapPin, LifeBuoy, Briefcase } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { MaskedLines, useSpotlight, EASE } from "@/components/motion";

const LINKEDIN_URL = "https://www.linkedin.com/company/enprex-services";

export default function Contact() {
  useSEO({
    title: "Contact EnPrex Services — Enquiries · Support · Partnerships",
    description: "Contact EnPrex Services directly by email, phone or LinkedIn — consulting enquiries, existing client support and partnership discussions.",
    path: "/contact",
  });

  const linkedinSpot = useSpotlight();

  return (
    <div data-testid="contact-page">
      {/* HERO */}
      <section className="container-x pt-16 pb-16 border-b border-white/5">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>
          <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-6 font-mono">— Contact</div>
          <h1 className="font-display text-5xl md:text-7xl font-light text-white tracking-tighter leading-[0.98] max-w-4xl">
            <MaskedLines
              delay={0.15}
              lines={[<>Let&apos;s discuss your</>, <span className="italic">requirements.</span>]}
            />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.6 }}
            className="mt-8 text-lg text-slate-300 max-w-3xl font-light leading-relaxed"
          >
            Reach the practice directly by email, phone or LinkedIn — no forms, no gatekeepers.
          </motion.p>
        </motion.div>
      </section>

      {/* LINKEDIN — primary channel */}
      <section className="container-x pt-16">
        <motion.a
          href={LINKEDIN_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="contact-linkedin"
          ref={linkedinSpot.ref}
          onMouseMove={linkedinSpot.onMouseMove}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="group relative block overflow-hidden border border-white/10 bg-gradient-to-br from-[#0B1F3A]/60 to-[#050A14] p-8 md:p-12 hover:border-[#D4AF37]/60 transition-colors duration-500"
        >
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            style={{ background: `radial-gradient(560px circle at ${linkedinSpot.pos.x}px ${linkedinSpot.pos.y}px, rgba(212,175,55,0.10), transparent 70%)` }}
          />
          <Linkedin
            aria-hidden
            className="absolute -right-6 -bottom-8 text-white/[0.04] group-hover:text-[#D4AF37]/[0.07] transition-colors duration-700"
            size={190}
            strokeWidth={1}
          />
          <div className="relative flex flex-col md:flex-row md:items-center gap-8">
            <div className="w-16 h-16 border border-[#D4AF37]/60 bg-[#D4AF37]/5 flex items-center justify-center shrink-0">
              <Linkedin className="text-[#D4AF37]" size={26} strokeWidth={1.4} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#D4AF37] mb-3">
                Connect professionally
              </div>
              <h2 className="font-display text-2xl md:text-4xl text-white font-light tracking-tight">
                EnPrex Services on <span className="italic">LinkedIn</span>
              </h2>
              <p className="mt-4 text-slate-400 leading-relaxed max-w-2xl">
                Follow the practice, see the latest thinking and start a conversation directly with the team.
              </p>
            </div>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] font-semibold text-[#D4AF37] group-hover:text-[#FDE047] transition-colors font-mono shrink-0">
              Visit LinkedIn <ArrowUpRight size={14} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </div>
          </div>
        </motion.a>
      </section>

      {/* PRIMARY CONTACTS — email only */}
      <section className="container-x py-16">
        <div className="grid lg:grid-cols-12 gap-8">
          {/* General Enquiries — primary */}
          <motion.a
            href="mailto:info@enprexservices.com"
            data-testid="contact-email-info"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: EASE }}
            className="min-w-0 lg:col-span-7 group border border-[#D4AF37]/30 bg-gradient-to-br from-[#0B1F3A]/50 to-[#050A14] p-8 md:p-12 hover:border-[#D4AF37]/70 hover:bg-white/[0.02] transition-all"
          >
            <div className="flex items-start gap-5 min-w-0">
              <div className="w-14 h-14 border border-[#D4AF37] flex-shrink-0 flex items-center justify-center">
                <Briefcase className="text-[#D4AF37]" size={22} strokeWidth={1.4} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#D4AF37] mb-3">
                  For new requirements, projects or consulting engagements
                </div>
                <h2 className="font-display text-xl sm:text-2xl md:text-4xl text-white font-light tracking-tight [overflow-wrap:anywhere]">
                  info@<wbr />enprexservices.com
                </h2>
                <p className="mt-5 text-slate-400 leading-relaxed max-w-2xl">
                  Consulting engagements, business enquiries, partnerships and general questions — write to us directly.
                </p>
                <div className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] font-semibold text-[#D4AF37] group-hover:text-[#FDE047] transition-colors font-mono">
                  Compose email <ArrowRight size={12} />
                </div>
              </div>
            </div>
          </motion.a>

          {/* Support */}
          <motion.a
            href="mailto:support@enprexservices.com"
            data-testid="contact-email-support"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.05, duration: 0.7, ease: EASE }}
            className="min-w-0 lg:col-span-5 group border border-white/10 bg-white/[0.02] p-8 md:p-12 hover:border-[#D4AF37]/40 hover:bg-white/[0.04] transition-all"
          >
            <div className="w-14 h-14 border border-white/15 flex items-center justify-center mb-6">
              <LifeBuoy className="text-[#D4AF37]" size={22} strokeWidth={1.4} />
            </div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#D4AF37] mb-3">
              For support with existing queries or projects
            </div>
            <h2 className="font-display text-xl sm:text-2xl md:text-3xl text-white font-light tracking-tight [overflow-wrap:anywhere]">
              support@<wbr />enprexservices.com
            </h2>
            <p className="mt-5 text-slate-400 leading-relaxed">
              Existing client support and project-related enquiries.
            </p>
            <div className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] font-semibold text-slate-400 group-hover:text-[#D4AF37] transition-colors font-mono">
              Compose email <ArrowRight size={12} />
            </div>
          </motion.a>
        </div>
      </section>

      {/* PARTNER WITH US — secondary */}
      <section id="partner" className="border-t border-white/5 bg-[#0B1F3A]/30">
        <div className="container-x py-20 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <Handshake className="text-[#D4AF37] mb-6" size={28} strokeWidth={1.3} />
            <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-6 font-mono">— Partner With Us</div>
            <h2 className="font-display text-3xl md:text-5xl font-light text-white tracking-tighter leading-[1.05]">
              Grow the practice <span className="italic">together</span>.
            </h2>
          </div>
          <div className="lg:col-span-7">
            <p className="text-lg text-slate-300 font-light leading-relaxed">
              EnPrex works with specialist consultants, technology providers and complementary consulting organizations to deliver expertise across ETRM, CTRM and energy trading technology.
            </p>
            <p className="mt-4 text-slate-400 leading-relaxed">
              We welcome conversations with independent ETRM/CTRM specialists, delivery partners, referral partners and complementary technology firms who share our standards of practitioner-led delivery.
            </p>
            <div className="mt-8">
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">
                Partnership enquiries
              </div>
              <a
                href="mailto:info@enprexservices.com?subject=Partnership%20Enquiry"
                data-testid="partner-email-info"
                className="inline-flex items-center gap-3 border border-white/20 text-white px-6 py-3 text-sm font-semibold uppercase tracking-[0.15em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
              >
                info@enprexservices.com <ArrowRight size={14} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CHANNELS */}
      <section className="container-x py-16 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 border border-white/5 bg-white/[0.02] p-8">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#D4AF37] mb-5">Channels</div>
          <div className="flex flex-wrap items-center gap-5">
            <a
              href="mailto:info@enprexservices.com"
              data-testid="channel-email-info"
              className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-[#D4AF37] transition-colors"
            >
              <Mail size={16} className="text-[#D4AF37]" /> info@enprexservices.com
            </a>
            <a
              href="mailto:support@enprexservices.com"
              data-testid="channel-email-support"
              className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-[#D4AF37] transition-colors"
            >
              <Mail size={16} className="text-[#D4AF37]" /> support@enprexservices.com
            </a>
            <a
              href="tel:+447893931916"
              data-testid="channel-phone"
              className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-[#D4AF37] transition-colors"
            >
              <Phone size={16} className="text-[#D4AF37]" /> +44 7893 931916
            </a>
          </div>
        </div>
        <div className="lg:col-span-4 border border-white/5 bg-white/[0.02] p-8">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#D4AF37] mb-5">Global reach</div>
          <div className="flex items-start gap-3 text-sm">
            <MapPin size={14} className="text-slate-500 mt-1" />
            <div>
              <div className="text-white font-medium">Remote-first consulting</div>
              <div className="text-xs text-slate-500 mt-0.5">Working with organisations across markets and time zones.</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
